import { useState, useCallback } from 'react';
import { validateAudioFile } from '../../services/audioService';
import { useLanguage, useTranslate } from '../../lib/i18n';
import type { RecordingState, VoiceRecordingError } from '../../components/VoiceRecorder';
import { useSpeechToText } from '../mutations/useSpeechToText';
import { extractErrorCode } from '../../utils/errorHandler';
import analyticsService from '../../services/analyticsService';

interface UseSpeechToTextFlowProps {
  onTranscriptionComplete?: (text: string, language?: string) => void;
  onError?: (error: VoiceRecordingError) => void;
  conversationId: string | null;
}

interface SpeechToTextFlowState {
  recordingState: RecordingState;
  transcribedText: string | null;
  isTranscribing: boolean;
  error: string | null;
  errorType?: 'permission' | 'technical' | 'network' | 'unknown';
}

interface SpeechToTextFlowActions {
  handleRecordingComplete: (audioBlob: Blob, mimeType: string) => void;
  handleRecordingCancel: () => void;
  handleRecordingError: (error: VoiceRecordingError) => void;
  handleRecordingStart: () => void;
  handlePermissionRequest: () => void;
  clearError: () => void;
}

/**
 * Maps API error codes to voice recording error types
 */
const mapApiErrorToVoiceErrorType = (errorCode: string): VoiceRecordingError['type'] => {
  switch (errorCode) {
    case 'network_error':
    case 'timeout':
      return 'network';
    case 'invalid_request':
    case 'validation_error':
      return 'technical';
    default:
      return 'technical';
  }
};

export const useSpeechToTextFlow = ({
  onTranscriptionComplete,
  onError,
  conversationId,
}: UseSpeechToTextFlowProps): SpeechToTextFlowState & SpeechToTextFlowActions => {
  const { language } = useLanguage();
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<
    'permission' | 'technical' | 'network' | 'unknown' | undefined
  >(undefined);

  const t = useTranslate();
  const { mutateAsync: transcribeAudio, isPending: isTranscribing } = useSpeechToText();

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
    setErrorType(undefined);
    if (recordingState === 'error') {
      setRecordingState('idle');
    }
  }, [recordingState]);

  // Handle permission request
  const handlePermissionRequest = useCallback(() => {
    setRecordingState('requesting-permission');
    setError(null);
    setErrorType(undefined);
  }, []);

  // Handle recording start
  const handleRecordingStart = useCallback(() => {
    setRecordingState('recording');
    setError(null);
  }, []);

  // Handle recording completion and start transcription
  const handleRecordingComplete = useCallback(
    async (audioBlob: Blob, mimeType: string) => {
      try {
        setError(null);
        setRecordingState('processing');

        // Validate audio file
        const validation = validateAudioFile(audioBlob, language);
        if (!validation.valid) {
          throw new Error(validation.error || 'Invalid audio file');
        }

        // Generate filename based on timestamp and MIME type
        const timestamp = Date.now();
        const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
        const fileName = `voice_recording_${timestamp}.${extension}`;

        if (!conversationId) {
          throw new Error('No conversation ID available for transcription');
        }

        // Transcribe audio (automatic retry handled by backend service)
        const transcriptionResult = await transcribeAudio({
          audioFile: audioBlob,
          fileName,
          language,
          conversationId,
        });

        // Store transcribed text for potential use
        setTranscribedText(transcriptionResult.text);
        setRecordingState('idle');

        // Track analytics for transcription result
        analyticsService.trackEvent({
          eventName: 'speechToTextTranscriptionSuccess',
          properties: {
            characterCount: transcriptionResult.text?.length || 0,
            isEmpty: !transcriptionResult.text || transcriptionResult.text.trim().length === 0,
            language,
            durationSeconds: Math.floor((Date.now() - timestamp) / 1000),
          },
        });

        // Call the callback to let parent handle the transcribed text
        onTranscriptionComplete?.(transcriptionResult.text, transcriptionResult.language);
      } catch (error) {
        // Use extractErrorCode to get standardized error type
        const errorCode = extractErrorCode(error);

        // Map error code to voice error type
        const voiceErrorType =
          errorCode === 'unknown_error' ? 'unknown' : mapApiErrorToVoiceErrorType(errorCode);

        // Get translated error message
        const errorMessage = t(`common.errors.${errorCode}`);

        // Create structured error
        const voiceError: VoiceRecordingError = {
          message: errorMessage,
          type: voiceErrorType,
        };

        setError(errorMessage);
        setErrorType(voiceErrorType);
        setRecordingState('error');
        onError?.(voiceError);
      }
    },
    [language, t, transcribeAudio, onTranscriptionComplete, onError]
  );

  // Handle recording cancellation
  const handleRecordingCancel = useCallback(() => {
    setRecordingState('idle');
    setError(null);
    setTranscribedText(null);
  }, []);

  // Handle recording errors
  const handleRecordingError = useCallback(
    (error: VoiceRecordingError) => {
      setError(error.message);
      setErrorType(error.type);
      setRecordingState('error');
      onError?.(error);
    },
    [onError]
  );

  return {
    // State
    recordingState,
    transcribedText,
    isTranscribing,
    error,
    errorType,

    // Actions
    handleRecordingComplete,
    handleRecordingCancel,
    handleRecordingError,
    handleRecordingStart,
    handlePermissionRequest,
    clearError,
  };
};
